import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  ChevronRight,
  ArrowRight,
  Clock,
  Phone,
  Mail,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import TopBar from "../components/TopBar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { buildBreadcrumb, SITE_URL } from "../lib/seoData";
import { getAllArticles } from "../data/knowledgeCenterData";

const KnowledgeCenter = () => {
  const articles = getAllArticles();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = [
    buildBreadcrumb([
      { name: "Home", path: "/" },
      { name: "Knowledge Center", path: "/knowledge-center" },
    ]),
    {
      "@type": "ItemList",
      name: "Crimson Inks Knowledge Center Articles",
      itemListElement: articles.map((a, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: a.title,
        url: `${SITE_URL}/knowledge-center/${a.slug}`,
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Knowledge Center – UV & LED Inks and Coatings Insights | Crimson Inks"
        description="Expert articles on UV and LED curable inks and coatings: curing technology, substrate selection, printing processes and choosing the right manufacturer."
        path="/knowledge-center"
        keywords="UV LED inks coatings articles, printing knowledge center, UV vs LED curing, ink coating selection guide, Crimson Inks insights"
        jsonLd={jsonLd}
      />
      <TopBar />

      {/* Header */}
      <header className="fixed top-10 left-0 right-0 z-40 bg-white shadow-lg py-3">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img
                src="/crimson-logo-with-name.png"
                alt="Crimson Inks"
                className="h-14 w-auto object-contain"
              />
            </Link>
            <nav className="hidden lg:flex items-center space-x-6">
              <Link to="/" className="text-gray-700 hover:text-[#8B1538] font-medium transition-colors">
                Home
              </Link>
              <span className="text-gray-400">/</span>
              <span className="text-[#8B1538] font-semibold">Knowledge Center</span>
            </nav>
            <Link to="/#contact">
              <Button className="bg-[#8B1538] hover:bg-[#DC143C] text-white px-6 py-2 rounded-md font-semibold">
                Contact Us
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-36 pb-16 bg-gradient-to-br from-[#8B1538] via-[#6d1029] to-[#4a0b1c] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <BookOpen className="h-5 w-5 mr-2" />
              <span className="text-sm font-medium">Knowledge Center</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Insights on UV & LED Inks and Coatings
            </h1>
            <p className="text-xl text-white/90 leading-relaxed max-w-3xl mx-auto">
              Practical, technical guidance from Crimson Inks on curing technology, substrate selection, printing processes and choosing the right ink and coating partner.
            </p>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {articles.map((article) => (
              <Link
                key={article.id}
                to={`/knowledge-center/${article.slug}`}
                className="group"
                data-testid={`article-card-${article.slug}`}
              >
                <Card className="h-full border-2 border-gray-100 hover:border-[#8B1538] transition-all duration-300 hover:shadow-xl group-hover:-translate-y-1">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-semibold text-[#8B1538] bg-[#8B1538]/10 px-3 py-1 rounded-full">
                        {article.category}
                      </span>
                      <span className="flex items-center text-xs text-gray-400">
                        <Clock className="h-3 w-3 mr-1" />
                        {article.readTime}
                      </span>
                    </div>
                    <h2 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[#8B1538] transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-sm text-gray-600 line-clamp-3 mb-4 flex-grow">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center text-[#8B1538] font-semibold text-sm">
                      Read Article
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#8B1538] to-[#DC143C]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Need a Tailored Recommendation?</h2>
            <p className="text-xl text-white/90 mb-8">
              Our ink and coating technologists can help you select or formulate the right product.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+917706994400"
                className="inline-flex items-center bg-white text-[#8B1538] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                <Phone className="h-5 w-5 mr-2" />
                +91 7706994400
              </a>
              <a
                href="mailto:Crimson.inks7475@gmail.com"
                className="inline-flex items-center bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-3 rounded-lg font-semibold hover:bg-white/20 transition-colors"
              >
                <Mail className="h-5 w-5 mr-2" />
                Email Our Experts
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default KnowledgeCenter;
